import { FormEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MessageSquare, Pencil } from 'lucide-react';
import {
  SupportApiClient,
  type SupportTicket,
  type SupportTicketAttachment,
  type SupportTicketSummary,
} from '@web-front/support-chat';

import {
  Alert,
  Button,
  EmptyState,
  Input,
  Modal,
  Textarea,
  BackButton,
  Heading,
  IconButton,
  CloseButton,
  Pressable,
  TabList,
  PaginationControls,
  Badge,
  useFilePicker,
} from '../shared/ui';
import { TrashIcon } from '../components/icons/trash';
import { useAppSelector } from '../hooks/redux';
import { selectCurrentToken } from '../store/auth.slice';

const supportApiBaseUrl = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_SUPPORT_API_BASE_URL;
const isProduction = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.MODE === 'production';
type ConfirmationAction = 'close' | 'delete' | null;
type TicketListTab = 'open' | 'completed';

const ticketListTabs: Array<{ id: TicketListTab; title: string }> = [
  { id: 'open', title: 'Открытые' },
  { id: 'completed', title: 'Завершённые' },
];

export default function SupportTickets() {
  const token = useAppSelector(selectCurrentToken);
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useMemo(
    () => new SupportApiClient({ apiBaseUrl: supportApiBaseUrl || '', getAccessToken: () => token || null }),
    [token],
  );
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const ticketListTab: TicketListTab = searchParams.get('tab') === 'completed' ? 'completed' : 'open';
  const ticketPage = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [subject, setSubject] = useState('');
  const [newContext, setNewContext] = useState('');
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const [reply, setReply] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationAction>(null);
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const listRequestRef = useRef(0);
  const ticketRequestRef = useRef(0);
  const readTicketRef = useRef<string | null>(null);
  const displayedTicket = ticket?.id === ticketId ? ticket : null;

  const loadList = useCallback(
    async (initial = false) => {
      if (!supportApiBaseUrl && isProduction) {
        setError('Сервис обращений не настроен. Обратитесь к администратору.');
        setInitialLoading(false);
        setRefreshing(false);
        return;
      }

      const requestId = ++listRequestRef.current;
      if (initial) setInitialLoading(true);
      else setRefreshing(true);
      try {
        const data = await api.listTickets({ tab: ticketListTab, page: ticketPage, limit: 10 });
        if (requestId !== listRequestRef.current) return;
        setTickets(data.items);
        setTotalPages(data.totalPages);
        setError(null);
      } catch {
        if (requestId === listRequestRef.current) {
          setError('Не удалось обновить обращения. Проверьте подключение и попробуйте ещё раз.');
        }
      } finally {
        if (requestId === listRequestRef.current) {
          if (initial) setInitialLoading(false);
          else setRefreshing(false);
        }
      }
    },
    [api, ticketListTab, ticketPage],
  );

  const loadTicket = useCallback(
    async (id: string, initial = false) => {
      if (!supportApiBaseUrl && isProduction) {
        setError('Сервис обращений не настроен. Обратитесь к администратору.');
        setInitialLoading(false);
        setRefreshing(false);
        return;
      }

      if (!id || id === 'undefined' || id === 'null') {
        setTicket(null);
        setInitialLoading(false);
        setError('Не удалось определить обращение. Откройте его из списка обращений.');
        return;
      }

      const requestId = ++ticketRequestRef.current;
      if (initial) setInitialLoading(true);
      else setRefreshing(true);
      try {
        const data = await api.getTicket(id);
        if (requestId !== ticketRequestRef.current) return;
        setTicket(data);
        setError(null);
      } catch {
        if (requestId === ticketRequestRef.current) {
          setTicket(null);
          setError('Не удалось открыть обращение. Вернитесь к списку и попробуйте ещё раз.');
        }
      } finally {
        if (requestId === ticketRequestRef.current) {
          if (initial) setInitialLoading(false);
          else setRefreshing(false);
        }
      }
    },
    [api],
  );

  useEffect(() => {
    readTicketRef.current = null;
    if (ticketId) void loadTicket(ticketId, true);
    else void loadList(true);
  }, [loadList, loadTicket, ticketId]);

  useEffect(() => {
    const interval = window.setInterval(() => (ticketId ? void loadTicket(ticketId) : void loadList()), 120_000);
    return () => window.clearInterval(interval);
  }, [loadList, loadTicket, ticketId]);

  useEffect(() => {
    if (!ticket?.unread || readTicketRef.current === ticket.id) return;
    readTicketRef.current = ticket.id;
    void api
      .markTicketRead(ticket.id)
      .then(() => {
        setTicket((current) => (current?.id === ticket.id ? { ...current, unread: false } : current));
      })
      .catch(() => {
        readTicketRef.current = null;
      });
  }, [api, ticket]);

  useEffect(() => {
    if (!showCreateDialog || !window.matchMedia('(min-width: 768px)').matches) return;
    subjectInputRef.current?.focus();
  }, [showCreateDialog]);

  useEffect(() => {
    if (!showCreateDialog || (!subject && !newContext)) return;
    const warnBeforeLeave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeave);
    return () => window.removeEventListener('beforeunload', warnBeforeLeave);
  }, [newContext, showCreateDialog, subject]);

  if (!supportApiBaseUrl && isProduction) {
    return (
      <section className="min-w-0 w-full max-w-full overflow-x-hidden pb-[85px]">
        <div className="mx-auto min-w-0 w-full max-w-[978px] px-4 sm:px-6">
          <Heading size="xl" className="text-pretty">
            Мои обращения
          </Heading>
          <p className="mt-4 text-ui text-primary sm:text-body-l" role="alert">
            Сервис обращений не настроен. Обратитесь к администратору.
          </p>
        </div>
      </section>
    );
  }

  const closeCreateDialog = () => {
    if (submitting) return;
    setShowCreateDialog(false);
    window.setTimeout(() => createButtonRef.current?.focus(), 0);
  };

  const createTicket = async (event: FormEvent) => {
    event.preventDefault();
    if (!subject.trim() || !newContext.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.createTicket({
        subject: subject.trim(),
        context: newContext.trim(),
        files: createFiles,
      });
      if (!created?.id) throw new Error('Support API returned a ticket without an identifier');
      setSubject('');
      setNewContext('');
      setCreateFiles([]);
      setShowCreateDialog(false);
      navigate(`/support-tickets/${created.id}`);
    } catch {
      setError('Не удалось создать обращение. Проверьте тему и сообщение, затем попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    if (!ticket?.id || (!reply.trim() && replyFiles.length === 0)) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.sendTicketMessage(ticket.id, { message: reply.trim(), files: replyFiles });
      setReply('');
      setReplyFiles([]);
      await loadTicket(ticket.id);
    } catch {
      setError('Не удалось отправить сообщение. Проверьте подключение и попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  };

  const reopenTicket = async () => {
    if (!ticket) return;
    setSubmitting(true);
    try {
      setTicket(await api.reopenTicket(ticket.id));
      setError(null);
    } catch {
      setError('Не удалось открыть обращение снова. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmAction = async () => {
    if (!ticket || !confirmation) return;
    setSubmitting(true);
    try {
      if (confirmation === 'close') {
        setTicket(await api.closeTicket(ticket.id));
      } else {
        await api.deleteTicket(ticket.id);
        navigate('/support-tickets', { replace: true });
      }
      setConfirmation(null);
      setError(null);
    } catch {
      setError(
        confirmation === 'delete'
          ? 'Не удалось удалить обращение. Попробуйте ещё раз.'
          : 'Не удалось закрыть обращение. Попробуйте ещё раз.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (ticketId) {
    return (
      <section className="min-w-0 w-full max-w-full overflow-x-hidden pb-[85px]" aria-busy={initialLoading}>
        <div className="mx-auto min-w-0 w-full max-w-[978px] px-4 sm:px-6">
          <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="flex min-w-0 items-center gap-5">
              <BackButton label="Вернуться к моим обращениям" className="shrink-0 m-1" to="/support-tickets" />
              <Heading size="xl" className="truncate">
                Мои обращения
              </Heading>
            </div>
            {displayedTicket ? (
              <TicketActions
                ticket={displayedTicket}
                submitting={submitting}
                onReopen={reopenTicket}
                onConfirm={setConfirmation}
              />
            ) : null}
          </div>
          {!displayedTicket ? <TicketDetailSkeleton /> : null}
          <LiveMessage error={error} refreshing={refreshing} />
          {displayedTicket ? (
            <TicketDetail
              api={api}
              ticket={displayedTicket}
              reply={reply}
              replyFiles={replyFiles}
              setReply={setReply}
              setReplyFiles={setReplyFiles}
              submitting={submitting}
              onSubmit={sendReply}
            />
          ) : null}
        </div>
        {confirmation ? (
          <ConfirmationDialog
            action={confirmation}
            submitting={submitting}
            onCancel={() => setConfirmation(null)}
            onConfirm={confirmAction}
          />
        ) : null}
      </section>
    );
  }

  return (
    <section className="min-w-0 w-full max-w-full overflow-x-hidden pb-[85px]" aria-busy={initialLoading}>
      <div className="mx-auto min-w-0 w-full max-w-[978px] px-4 sm:px-6">
        <div className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <Heading size="xl" className="text-pretty">
              Мои обращения
            </Heading>
            <p className="mt-2 max-w-2xl text-ui text-ink-muted sm:text-body-l">
              Переписка с поддержкой и ответы специалистов.
            </p>
          </div>
          <Button
            ref={createButtonRef}
            type="button"
            size="lg"
            className="w-full sm:w-auto"
            onClick={() => setShowCreateDialog(true)}
          >
            Новое обращение
          </Button>
        </div>
        <LiveMessage error={error} refreshing={refreshing} />
        <TabList
          className="mb-5"
          items={ticketListTabs.map((tab) => ({ id: tab.id, label: tab.title }))}
          value={ticketListTab}
          onChange={(tab) => setSearchParams({ tab, page: '1' })}
        />
        {initialLoading ? (
          <TicketSkeleton />
        ) : tickets.length === 0 ? (
          <EmptyTickets tab={ticketListTab} onCreate={() => setShowCreateDialog(true)} />
        ) : (
          <>
            <div className="rounded-card border border-surface-muted bg-white p-4 shadow-raised sm:p-6">
              <div className="grid min-w-0 max-w-full grid-cols-[minmax(0,1fr)] gap-4">
                {tickets.map((item) => (
                  <TicketRow key={item.id} ticket={item} />
                ))}
              </div>
            </div>
            <PaginationControls
              label="Страницы обращений"
              className="mt-5"
              page={ticketPage}
              totalPages={totalPages}
              onPageChange={(page) => setSearchParams({ tab: ticketListTab, page: String(page) })}
            />
          </>
        )}
      </div>
      {showCreateDialog ? (
        <CreateTicketDialog
          subject={subject}
          setSubject={setSubject}
          context={newContext}
          setContext={setNewContext}
          files={createFiles}
          setFiles={setCreateFiles}
          submitting={submitting}
          subjectInputRef={subjectInputRef}
          onSubmit={createTicket}
          onClose={closeCreateDialog}
        />
      ) : null}
    </section>
  );
}

function TicketRow({ ticket }: { ticket: SupportTicketSummary }) {
  return (
    <Link
      to={`/support-tickets/${ticket.id}`}
      style={{ contentVisibility: 'auto' }}
      className="block min-h-24 min-w-0 max-w-full overflow-hidden touch-manipulation rounded-card border border-surface-muted bg-white p-4 transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <div className="flex min-w-0 items-center gap-2">
        <strong className="min-w-0 flex-1 truncate text-body-l-strong text-ink sm:text-heading-md">
          {ticket.subject}
        </strong>
        {ticket.unread ? (
          <Badge tone="danger" className="shrink-0">
            Новый ответ
          </Badge>
        ) : null}
      </div>
      <div className="mt-2 flex min-w-0 items-center gap-2 overflow-hidden text-ui-xs text-ink-muted sm:text-ui">
        <span className="shrink-0">{ticket.source === 'chat_escalation' ? 'ИИ-помощник' : 'Вы'}</span>
        <span aria-hidden="true">•</span>
        <TicketStatusBadge status={ticket.status} />
        <span aria-hidden="true">•</span>
        <time className="shrink-0">{formatDate(ticket.updatedAt)}</time>
      </div>
      {ticket.lastMessagePreview ? (
        <p className="mt-2 truncate text-ui text-ink-muted">{ticket.lastMessagePreview}</p>
      ) : null}
    </Link>
  );
}

function TicketActions({
  ticket,
  submitting,
  onReopen,
  onConfirm,
}: {
  ticket: SupportTicket;
  submitting: boolean;
  onReopen: () => void;
  onConfirm: (action: ConfirmationAction) => void;
}) {
  const closed = ticket.status === 'closed';
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3">
      {closed ? (
        <Button variant="primary" disabled={submitting} loading={submitting} onClick={onReopen} size="md">
          Открыть снова
        </Button>
      ) : (
        <Button variant="neutral" disabled={submitting} onClick={() => onConfirm('close')} size="md">
          Закрыть обращение
        </Button>
      )}
      <IconButton
        label="Удалить обращение"
        size="md"
        className="group"
        disabled={submitting}
        onClick={() => onConfirm('delete')}
      >
        <TrashIcon width={28} height={28} stroke="currentColor" className="text-ink-subtle group-hover:text-primary" />
      </IconButton>
    </div>
  );
}

function TicketDetail({
  api,
  ticket,
  reply,
  replyFiles,
  setReply,
  setReplyFiles,
  submitting,
  onSubmit,
}: {
  api: SupportApiClient;
  ticket: SupportTicket;
  reply: string;
  replyFiles: File[];
  setReply: (value: string) => void;
  setReplyFiles: (value: File[]) => void;
  submitting: boolean;
  onSubmit: (event: FormEvent) => void;
}) {
  const closed = ticket.status === 'closed';
  const chronologicalMessages = [...ticket.messages].sort(
    (left, right) =>
      new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime() || left.id.localeCompare(right.id),
  );
  return (
    <article className="animate-fade-in rounded-card bg-white p-4 shadow-raised sm:p-6 lg:rounded-popup lg:p-10">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <Heading size="sm" as="h1" className="min-w-0 break-words text-pretty">
          {ticket.subject}
        </Heading>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <Badge tone="neutral">{ticket.source === 'chat_escalation' ? 'Создано ИИ-помощником' : 'Создано вами'}</Badge>
          <TicketStatusBadge status={ticket.status} />
        </div>
      </header>
      {ticket.contextSummary || ticket.attachments.length ? (
        <section className="mb-5 rounded-card bg-surface-muted p-4">
          {ticket.contextSummary ? (
            <p className="break-words whitespace-pre-wrap text-ui text-ink-muted sm:text-body-l">
              {ticket.contextSummary}
            </p>
          ) : null}
          <AttachmentList api={api} ticketId={ticket.id} attachments={ticket.attachments} />
        </section>
      ) : null}
      <div className="grid gap-3" role="log" aria-live="polite">
        {chronologicalMessages.map((message) => (
          <article
            key={message.id}
            className={`max-w-[92%] break-words rounded-card p-4 sm:max-w-[80%] ${message.author === 'support' ? 'border border-danger-line bg-danger-surface text-ink' : 'justify-self-end bg-accent text-ink'}`}
          >
            {message.author === 'support' ? (
              <p className="mb-2 text-ui-xs-strong text-primary">Ответ специалиста поддержки</p>
            ) : null}
            {message.content ? (
              <p className="whitespace-pre-wrap text-ui leading-relaxed sm:text-body-l">{message.content}</p>
            ) : null}
            <AttachmentList api={api} ticketId={ticket.id} attachments={message.attachments} />
            <time className="mt-3 block text-ui-xs opacity-70">{formatDate(message.createdAt)}</time>
          </article>
        ))}
      </div>
      {!closed ? (
        <form onSubmit={onSubmit} className="mt-6 grid gap-3 border-t border-line pt-5">
          <Textarea
            label="Ваш ответ"
            name="ticket-reply"
            autoComplete="off"
            value={reply}
            maxLength={4000}
            rows={5}
            onChange={(event) => setReply(event.target.value)}
          />
          <FilePicker files={replyFiles} setFiles={setReplyFiles} />
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || (!reply.trim() && replyFiles.length === 0)}
            loading={submitting}
            size="md"
            fullWidth
            className="sm:w-auto"
          >
            Отправить сообщение
          </Button>
        </form>
      ) : (
        <p className="mt-5 rounded-card bg-surface-muted p-4 text-ui text-ink-muted sm:text-body-l">
          Переписка закрыта. Если вопрос снова актуален, откройте обращение.
        </p>
      )}
    </article>
  );
}

function FilePicker({ files, setFiles }: { files: File[]; setFiles: (value: File[]) => void }) {
  const [error, setError] = useState<string | null>(null);
  const onFiles = (selected: File[]) => {
    const next = [...files, ...selected];
    if (next.length > 5) setError('Можно прикрепить не больше 5 файлов.');
    else if (next.some((file) => file.size > 10 * 1024 * 1024)) {
      setError('Размер каждого файла не должен превышать 10 МБ.');
    } else {
      setFiles(next);
      setError(null);
    }
  };
  const picker = useFilePicker({
    multiple: true,
    accept:
      'image/jpeg,image/png,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain',
    onFiles,
  });
  return (
    <div className="grid gap-2">
      {picker.input}
      <Button size="md" variant="secondary" className="w-max" onClick={picker.open}>
        Прикрепить файлы
      </Button>
      <p className="text-ui-xs text-ink-muted">JPEG, PNG, WebP, PDF, DOCX, XLSX, TXT — до 10 МБ, максимум 5 файлов.</p>
      {files.length ? (
        <ul className="grid gap-2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.lastModified}-${index}`}
              className="flex min-w-0 items-center gap-2 rounded-control bg-surface-muted px-3 py-2 text-ui"
            >
              <span className="min-w-0 flex-1 truncate">
                {file.name} · {formatFileSize(file.size)}
              </span>
              <CloseButton
                size="sm"
                label={`Удалить файл ${file.name}`}
                onClick={() => setFiles(files.filter((_, current) => current !== index))}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <p className="text-ui text-primary" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function AttachmentList({
  api,
  ticketId,
  attachments,
}: {
  api: SupportApiClient;
  ticketId: string;
  attachments: SupportTicketAttachment[];
}) {
  if (!attachments.length) return null;
  return (
    <ul className="mt-3 grid gap-2">
      {attachments.map((attachment) => (
        <AttachmentItem key={attachment.id} api={api} ticketId={ticketId} attachment={attachment} />
      ))}
    </ul>
  );
}

function AttachmentItem({
  api,
  ticketId,
  attachment,
}: {
  api: SupportApiClient;
  ticketId: string;
  attachment: SupportTicketAttachment;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const image = attachment.mimeType.startsWith('image/');
  useEffect(() => {
    if (!image) return;
    let active = true;
    let url: string | null = null;
    void api
      .getTicketAttachment(ticketId, attachment.id)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        if (active) setPreviewUrl(url);
        else URL.revokeObjectURL(url);
      })
      .catch(() => undefined);
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, attachment.id, image, ticketId]);
  const download = async () => {
    const blob = await api.getTicketAttachment(ticketId, attachment.id);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.name;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <li className="min-w-0">
      <Pressable
        onClick={() => void download()}
        className="flex w-full min-w-0 items-center gap-3 rounded-control border border-current/10 bg-white/70 p-2 text-left transition-colors hover:bg-white"
      >
        {image && previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            width={48}
            height={48}
            loading="lazy"
            className="size-12 shrink-0 rounded-chip object-cover"
          />
        ) : (
          <span
            className="flex size-12 shrink-0 items-center justify-center rounded-chip bg-surface-muted text-body-xl"
            aria-hidden="true"
          >
            ⌁
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui-strong">{attachment.name}</span>
          <span className="mt-1 block text-ui-xs opacity-70">{formatFileSize(attachment.sizeBytes)}</span>
        </span>
      </Pressable>
    </li>
  );
}

function CreateTicketDialog({
  subject,
  setSubject,
  context,
  setContext,
  files,
  setFiles,
  submitting,
  subjectInputRef,
  onSubmit,
  onClose,
}: {
  subject: string;
  setSubject: (value: string) => void;
  context: string;
  setContext: (value: string) => void;
  files: File[];
  setFiles: (value: File[]) => void;
  submitting: boolean;
  subjectInputRef: RefObject<HTMLInputElement>;
  onSubmit: (event: FormEvent) => void;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen
      onClose={submitting ? undefined : onClose}
      fullscreenOnMobile
      closeOnBackdrop
      title="Новое обращение"
      description="Опишите вопрос — ответ специалиста появится в этой переписке."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <Input
          ref={subjectInputRef}
          size="lg"
          label="Тема"
          name="ticket-subject"
          autoComplete="off"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          minLength={2}
          maxLength={160}
          required
        />
        <Textarea
          label="Описание обращения"
          name="ticket-context"
          autoComplete="off"
          value={context}
          onChange={(event) => setContext(event.target.value)}
          maxLength={4000}
          required
          rows={7}
        />
        <FilePicker files={files} setFiles={setFiles} />
        <div className="mt-auto flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="neutral" disabled={submitting} onClick={onClose} size="lg" fullWidth className="sm:w-auto">
            Отмена
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            loading={submitting}
            size="lg"
            fullWidth
            className="sm:w-auto"
          >
            Создать обращение
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ConfirmationDialog({
  action,
  submitting,
  onCancel,
  onConfirm,
}: {
  action: Exclude<ConfirmationAction, null>;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const deleting = action === 'delete';
  return (
    <Modal
      isOpen
      onClose={submitting ? undefined : onCancel}
      size="sm"
      closeOnBackdrop
      title={deleting ? 'Удалить обращение?' : 'Закрыть обращение?'}
      footer={
        <>
          <Button variant="neutral" disabled={submitting} onClick={onCancel} size="md" className="flex-1">
            Отмена
          </Button>
          <Button
            variant={deleting ? 'dark' : 'primary'}
            disabled={submitting}
            loading={submitting}
            onClick={onConfirm}
            size="md"
            className="flex-1"
          >
            {deleting ? 'Удалить обращение' : 'Закрыть обращение'}
          </Button>
        </>
      }
    >
      <p className="text-ui leading-relaxed text-ink-muted sm:text-body-l">
        {deleting
          ? 'Обращение исчезнет из вашего списка и чата. Поддержка сохранит его для проверки, восстановить в интерфейсе нельзя.'
          : 'Переписка станет доступной только для чтения. При необходимости вы сможете открыть обращение снова.'}
      </p>
    </Modal>
  );
}

function TicketStatusBadge({ status }: { status: SupportTicketSummary['status'] }) {
  const labels = { open: 'Открыто', waiting_for_user: 'Ждём вас', resolved: 'Решено', closed: 'Закрыто' } as const;
  const tones = { open: 'warning', waiting_for_user: 'info', resolved: 'success', closed: 'neutral' } as const;
  return (
    <Badge tone={tones[status]} className="shrink-0">
      {labels[status]}
    </Badge>
  );
}

function EmptyTickets({ tab, onCreate }: { tab: TicketListTab; onCreate: () => void }) {
  if (tab === 'completed') {
    return (
      <EmptyState icon={<MessageSquare size={24} strokeWidth={1.5} />} title="Завершённых обращений пока нет">
        Здесь появятся решённые и закрытые обращения.
      </EmptyState>
    );
  }
  return (
    <EmptyState
      icon={<MessageSquare size={24} strokeWidth={1.5} />}
      title="Обращений нет"
      action={
        <Button size="md" variant="outline" startIcon={<Pencil size={18} />} onClick={onCreate}>
          Написать в поддержку
        </Button>
      }
    >
      Если что-то не работает, напишите нам — ответим как можно быстрее.
    </EmptyState>
  );
}

function TicketSkeleton() {
  return (
    <div className="grid min-w-0 max-w-full grid-cols-[minmax(0,1fr)] gap-3" aria-label="Загружаем обращения">
      <div className="h-24 animate-pulse rounded-card bg-white" />
      <div className="h-24 animate-pulse rounded-card bg-white" />
      <div className="h-24 animate-pulse rounded-card bg-white" />
    </div>
  );
}
function TicketDetailSkeleton() {
  return (
    <div
      className="min-h-[360px] animate-pulse rounded-card bg-white shadow-raised sm:min-h-[440px] lg:rounded-popup"
      aria-label="Загружаем обращение"
    />
  );
}
function LiveMessage({ error, refreshing }: { error: string | null; refreshing: boolean }) {
  return (
    <div className="mb-4 min-h-5" aria-live="polite">
      {error ? (
        <Alert tone="danger">{error}</Alert>
      ) : refreshing ? (
        <span className="text-ui text-ink-muted">Обновляем…</span>
      ) : null}
    </div>
  );
}
function formatDate(value: string) {
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}
function formatFileSize(value: number) {
  return (
    new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(
      value < 1024 * 1024 ? value / 1024 : value / (1024 * 1024),
    ) + (value < 1024 * 1024 ? ' КБ' : ' МБ')
  );
}
